import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import { loadAdtNotifications, PAGE_SIZES } from './api';
import { Button, DropdownButton } from '../../../components';
import { AdtRow } from './components';

const PatientSearch = ({
	user,
	onShowCareTeam,
	onOpenInstavisit,
	onShowNotes,
	onShowPatientADTs,
	selectedAdt,
	onShowCCDA,
	hasCampaigns = false,
	onOpenAddToCampaign,
	isCampaignModalOpen,
	onShowAdditionalDetails,
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [data, setData] = useState([]);
	const [hasSearched, setHasSearched] = useState(false);

	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [birthDate, setBirthDate] = useState('');

	const [offset, setOffset] = useState(0);
	const [resultCount, setResultCount] = useState(0);
	const [recordLimit, setRecordLimit] = useState(PAGE_SIZES[0]);

	useEffect(() => {
		if (resultCount > 0) {
			setIsLoading(true);
			setHasSearched(true);
			loadAdtNotifications(user.id, {
				first_name: firstName,
				last_name: lastName,
				dob: birthDate,
				offset,
				isPatientSearch: true,
				record_limit: recordLimit,
			}).then(({ acrs_result, acrs_count }) => {
				setIsLoading(false);
				setData(acrs_result);
				setResultCount(acrs_count[0].result_count);
			});
		}
	}, [offset, recordLimit]);

	useEffect(() => {
		if (!isCampaignModalOpen && resultCount > 0) {
			setIsLoading(true);
			setHasSearched(true);
			loadAdtNotifications(user.id, {
				first_name: firstName,
				last_name: lastName,
				dob: birthDate,
				offset,
				isPatientSearch: true,
				record_limit: recordLimit,
			}).then(({ acrs_result, acrs_count }) => {
				setIsLoading(false);
				setData(acrs_result);
				setResultCount(acrs_count[0].result_count);
			});
		}
	}, [isCampaignModalOpen]);

	const handleSearchClicked = () => {
		setIsLoading(true);
		setHasSearched(true);
		if (offset > 0) {
			setOffset(0);
		} else {
			loadAdtNotifications(user.id, {
				first_name: firstName,
				last_name: lastName,
				dob: birthDate,
				offset: 0,
				isPatientSearch: true,
				record_limit: recordLimit,
			}).then(({ acrs_result, acrs_count }) => {
				// console.log(acrs_count[0].result_count)
				setIsLoading(false);
				setData(acrs_result);
				setResultCount(acrs_count[0].result_count);
			});
		}
	};

	const handleFormSubmission = (event) => {
		event.preventDefault();
		handleSearchClearClicked();
	};

	const handleSearchClearClicked = () => {
		setFirstName('');
		setLastName('');
		setBirthDate('');
		setHasSearched(false);
		setData([]);
		setOffset(0);
		setResultCount(0);
	};

	const handlePreviousClick = () => {
		let newOffset = offset - recordLimit;
		if (newOffset < 0) {
			newOffset = 0;
		}
		setOffset(newOffset);
	};

	const handleNextClick = () => {
		setOffset(offset + recordLimit);
	};

	const handleRecordLimitChange = (newRecordLimit) => {
		setRecordLimit(newRecordLimit);
	};

	return (
		<div className="content-wrapper patient-search">
			<ol className="breadcrumb">
				<li>
					<Link to="/">Home</Link>
				</li>
				<li>ADT</li>
				<li className="active">Patient Search</li>
			</ol>
			<div className="panel panel-default">
				<div className="panel-heading adt-header">
					<form className="row" onSubmit={handleFormSubmission}>
						<div className="col-md-12 text-center form-inline">
							<div className="form-group mr-lg">
								<label className="mr">First Name</label>
								<input
									type="text"
									className="form-control"
									value={firstName}
									onChange={({ target }) => setFirstName(target.value)}
								/>
							</div>
							<div className="form-group mr-lg">
								<label className="mr">Last Name</label>
								<input
									type="text"
									className="form-control"
									value={lastName}
									onChange={({ target }) => setLastName(target.value)}
								/>
							</div>
							<div className="form-group mr-lg">
								<label className="mr">Date of Birth</label>
								<input
									type="date"
									className="form-control"
									value={birthDate}
									onChange={({ target }) => setBirthDate(target.value)}
								/>
							</div>
							<div className="btn-group">
								<Button
									onClick={handleSearchClicked}
									title="Search"
									disabled={isLoading}
								/>
								{data.length > 0 && (
									<Button
										disabled={isLoading}
										icon="times-circle"
										altTitle="Clear Search Parameters"
										onClick={handleSearchClearClicked}
									/>
								)}
							</div>
						</div>
					</form>
					{hasSearched && data.length > 0 && (
						<div className="row mt-md">
							<div className="col-md-3 h4">
								Showing{' '}
								{isLoading ? (
									<em className="fa fa-spin fa-circle-o-notch" />
								) : (
									`${offset + 1} - ${offset + data.length} of ${resultCount}`
								)}
							</div>
							<div className="col-md-6 text-center pt">
								<div className="btn-group" role="group" aria-label="...">
									<Button
										icon="arrow-left"
										onClick={handlePreviousClick}
										disabled={offset === 0}
										altTitle="Previous"
									/>
									<div className="btn-group" role="group">
										<DropdownButton
											title={`Showing ${recordLimit || '10'} records`}
											items={PAGE_SIZES.map((pageSize) => ({
												title: `${pageSize} per page`,
												onClick: () => handleRecordLimitChange(pageSize),
											}))}
										/>
									</div>
									<Button
										icon="arrow-right"
										onClick={handleNextClick}
										disabled={data.length < recordLimit}
										altTitle="Next"
									/>
								</div>
							</div>
							<div className="col-md-3" />
						</div>
					)}
				</div>
				<div className="panel-body adt-list">
					{!hasSearched && !isLoading && (
						<div className="row">
							<div className="col-md-12 m">
								Enter some patient information and click Search to get started.
							</div>
						</div>
					)}
					{hasSearched && !isLoading && data.length === 0 && (
						<div className="row">
							<div className="col-md-12 m">
								No data found. Change your search criteria and try again.
							</div>
						</div>
					)}
					{data.map((adt) => (
						<AdtRow
							key={adt.id}
							adt={adt}
							isLoading={isLoading}
							userId={user.get('id')}
							selectedAdt={selectedAdt}
							onShowNotes={onShowNotes}
							onShowCareTeam={onShowCareTeam}
							onOpenInstavisit={onOpenInstavisit}
							onShowPatientADTs={onShowPatientADTs}
							onShowCCDA={onShowCCDA}
							hasCampaigns={hasCampaigns}
							onOpenAddToCampaign={onOpenAddToCampaign}
							onShowAdditionalDetails={onShowAdditionalDetails}
						/>
					))}
				</div>
			</div>
		</div>
	);
};

PatientSearch.propTypes = {
	user: PropTypes.object.isRequired,
	onShowCareTeam: PropTypes.func.isRequired,
	onOpenInstavisit: PropTypes.func.isRequired,
	onShowNotes: PropTypes.func.isRequired,
	onShowPatientADTs: PropTypes.func.isRequired,
	selectedAdt: PropTypes.object,
	onShowCCDA: PropTypes.func,
	hasCampaigns: PropTypes.bool,
	onOpenAddToCampaign: PropTypes.func,
	isCampaignModalOpen: PropTypes.bool,
	onShowAdditionalDetails: PropTypes.func,
};

export default PatientSearch;
